import { SplitText } from 'gsap/SplitText'

/**
 * One SplitText instance per text element, shared by its in- and out-animation.
 *
 * The split keeps the richtext markup: SplitText only wraps text nodes, so links, underlines and
 * styled spans survive. A richtext element holds block children (`<p>`), and SplitText measures
 * lines per target, so the blocks are the targets; `split.lines` then lists the lines of all
 * blocks in reading order.
 *
 * Lines depend on layout, so a line split re-runs after a font load or a width change
 * (`autoSplit`). The wrappers of the previous split are gone then and every tween that targeted
 * them is rebuilt by the rebuilders the timeline registered through `onTextResplit`.
 */

const SPLIT_KEY = '_maglrTextSplit'
const SPLIT_TYPES = ['chars', 'words', 'lines']
const BLOCK_TAGS = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'DIV']
const DEFAULT_SPLIT = 'words'

export const CLASS_CHAR = 'text-animation-char'
export const CLASS_WORD = 'text-animation-word'
export const CLASS_LINE = 'text-animation-line'

export function resolveSplitType (settings) {
    return SPLIT_TYPES.includes(settings.split) ? settings.split : DEFAULT_SPLIT
}

// SplitText wraps chars inside words, so a char split always carries the word split.
function splitTypeList (types) {
    const wanted = new Set(types)
    if (wanted.has('chars')) wanted.add('words')
    return SPLIT_TYPES.filter(type => wanted.has(type))
}

function splitTargets (el) {
    const blocks = Array.from(el.children).filter(child => BLOCK_TAGS.includes(child.tagName))
    return blocks.length > 0 ? blocks : [el]
}

/**
 * Split `el` for the given split types, reusing an existing split when it already covers them.
 * Called once per element before its tweens are built; the rebuilders of an earlier timeline are
 * dropped because their tweens no longer exist.
 */
export function prepareTextSplit (el, types) {
    const wanted = splitTypeList(types)
    const existing = el[SPLIT_KEY]
    if (existing && wanted.every(type => existing.types.includes(type))) {
        existing.rebuilders = []
        return existing
    }
    existing?.split.revert()

    const context = { types: wanted, rebuilders: [], split: null }
    context.split = SplitText.create(splitTargets(el), {
        type: wanted.join(','),
        tag: 'span',
        aria: 'auto',
        deepSlice: true,
        autoSplit: wanted.includes('lines'),
        charsClass: CLASS_CHAR,
        wordsClass: CLASS_WORD,
        linesClass: CLASS_LINE,
        onSplit: () => context.rebuilders.forEach(rebuild => rebuild()),
    })
    el[SPLIT_KEY] = context
    return context
}

export function getTextTargets (el, splitType) {
    return el[SPLIT_KEY]?.split[splitType] ?? []
}

export function getAllTextTargets (el) {
    const split = el[SPLIT_KEY]?.split
    return split ? [...split.chars, ...split.words, ...split.lines] : []
}

export function onTextResplit (el, rebuild) {
    el[SPLIT_KEY]?.rebuilders.push(rebuild)
}

export function revertTextSplit (el) {
    el[SPLIT_KEY]?.split.revert()
    delete el[SPLIT_KEY]
}
